import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";
import { Button } from "@/components/design/Button";
import { useUnsavedEditsGate } from "@/lib/unsaved-edits-gate";
import { useCompanyContext } from "@/pages/company/use-company-context";

// The standing statement of which company the person is acting as
// (company-profiles/0002-company-account-model §5), under the topbar on every page for as long as
// the mode is on.
//
// AMBIENT MODE, AMBIENT STATEMENT. The mode is the one thing on this platform that is true of the
// whole session rather than of the page in front of you, so it is said in the chrome rather than on
// the company's page: a person who navigates to /save while acting as a company must still be able
// to see that they are, and to stop.
//
// NEUTRAL, BY THE CONVENTION THIS CODEBASE ENFORCES. No accent ring, no coloured border, no danger
// token — those are for buttons and destructive confirmations, and an accent-ringed "acting as"
// banner is a review blocker here. The mode is signalled the way every other state in spark is
// signalled: in words, on the neutral wash rung (`bg-ink/[0.02]`), with the company's name doing
// the work. Nothing about it is a warning, because nothing about it is wrong.
//
// ONE SCOPE SENTENCE, NOT TWO, AND IT DOES NOT READ THE ROLE. It used to branch: @adr SPARK-0002-company-account-model@1 §6
// describes what an ADMIN may do in this mode and says nothing about a member, so a member got a
// cautious second wording rather than being told they may edit. Concept v10 made the context itself
// admin-only (use-company-context resolves nothing for any other role), which left that arm
// unreachable — copy no reader could ever be shown, and a test that passed only because the hook was
// mocked. Branch, message and test are gone together.
//
// So `active` here is always an admin's, and the sentence says what §6 permits one — ALL FOUR things,
// including publishing in the company's name. That last clause was missing until genesis#263 was
// reviewed against the concept: §6 gained it in v7 and the standing statement of the mode never
// caught up, so the one capability with consequences a stranger can see was the one it did not name. What it permits
// is still decided per mutation by genesis (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization); this is a statement, never a capability.
//
// It renders NOTHING while the stored id is still being resolved against genesis, so a name never
// appears before the server has confirmed the membership behind it (see use-company-context).
//
// LEAVING ASKS FIRST WHEN SOMETHING IS UNSAVED. The cache reset that @adr SPARK-0034-acting-company-context-resets-the-apollo-cache@1 requires is correct
// and stays exactly as it is — but it remounts every editor on screen, and an editor's in-progress
// form state does not survive that. Since the URL never moves, UnsavedGuard's router blocker never
// fires either, so this control holds itself through the same PendingEdits registry instead.

export function CompanyContextBar() {
  const intl = useIntl();
  const { active, leave } = useCompanyContext();
  const gate = useUnsavedEditsGate();

  if (!active) return null;

  return (
    <>
      <div
        className="bg-ink/[0.02] px-4 py-2 sm:px-6"
        role="region"
        aria-label={intl.formatMessage({
          id: "company.context.landmark",
          defaultMessage: "Company context",
        })}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <p className="field-meta min-w-0 text-ink/55">
            <FormattedMessage
              id="company.context.acting"
              defaultMessage="Acting as {name}."
              values={{
                name: (
                  // The name links to the company's own page, which is where everything @adr SPARK-0002-company-account-model@1 §6
                  // permits in this mode actually happens.
                  <Link to={`/c/${active.id}`} className="text-ink/80 underline underline-offset-2">
                    {active.name}
                  </Link>
                ),
              }}
            />{" "}
            <FormattedMessage
              id="company.context.scopeAdmin"
              defaultMessage="You can view and edit this company, manage its people, and publish in its name."
            />
          </p>
          <Button variant="chip" size="sm" onClick={() => gate.request(leave)}>
            <FormattedMessage id="company.context.leave" defaultMessage="Leave company context" />
          </Button>
        </div>
      </div>
      {/* Outside the region on purpose: the bar is a standing statement, and a modal question about
          unsaved work is not part of it. */}
      <UnsavedChangesDialog
        open={gate.blocked}
        body={
          <FormattedMessage
            id="company.context.unsavedBody"
            defaultMessage="Changing the company context reloads everything on screen. Changes you have not saved will be lost."
          />
        }
        saving={gate.saving}
        error={gate.error}
        onStay={gate.stay}
        onDiscard={gate.discardAndProceed}
        onSave={gate.saveAndProceed}
      />
    </>
  );
}
